import Vue from "vue"
import Helpers from "@/helpers.js"

const bucketRoute = (ct, b) => `/case-types/${ct.id}/buckets/${b.id}`
const bucketGroupRoute = (ct, bg) => `/case-types/${ct.id}/bucket-groups/${bg.id}`
const templateOverviewRoute = (ct) => `/case-types/${ct.id}/overview`

const isCaseblocksEmail = (email) => {
  if (typeof email !== "string") return false
  const at = email.lastIndexOf("@")
  if (at === -1) return false
  return email.slice(at + 1).toLowerCase() === "caseblocks.com"
}

const canSeeQueues = (user) => {
  if (!user || !user.isAccountAdmin) return false
  const email = user.email || (user.me && user.me.email)
  return isCaseblocksEmail(email)
}

const queuesMenuItem = {
  title: "Queues",
  icon: { code: "layer-group", set: "fas" },
  route: "/admin/queues",
}

const accountAdminMenuItems = (isAppAdmin, includeChat) => {
  const items = [
    { title: "Details", route: "/account-menu/details" },
  ]
  if (isAppAdmin) {
    items.push({ title: "Features", route: "/account-menu/features" })
  }
  items.push(
    { title: "Security", route: "/account-menu/security" },
    { title: "Emails", route: "/account-menu/emails" },
    { title: "Layout", route: "/account-menu/layout" },
  )
  if (includeChat) {
    items.push({ title: "Chat", route: "/account-menu/chat" })
  }
  items.push({ title: "Runtime", route: "/account-menu/runtime" })
  return items
}

const appendAuditNav = (configMenu, auditEnabled) => {
  if (!auditEnabled) return
  configMenu.push({
    title: "Audit",
    icon: { code: "history", set: "fas" },
    route: "/admin/audit",
  })
}

export default {
  bookmarkAdded(state, bookmark) {
    const menu = state.caseWorkMenu
    menu[0].items[0].childItems.push({ id: bookmark.id, route: bookmark.route, title: bookmark.linkText })

    state.caseWorkMenu.splice(0, 1, menu[0])
  },
  bookmarkRemoved(state, data) {
    let index = -1
    let i = 0
    const RemoveId = data.id
    const bookmarks = state.caseWorkMenu[0].items[0].childItems

    for (i in bookmarks) {
      if (parseInt(bookmarks[i].id) === parseInt(RemoveId)) {
        index = i
        break
      } else {
        index = -1
      }
    }
    if (index >= 0) {
      state.caseWorkMenu[0].items[0].childItems.splice(index, 1)
    }
  },
  requestActiveUser(state) {
    state.loadingActiveUser = true
  },
  receivedActiveUser(state) {
    state.loadingActiveUser = false
  },
  requestAllUsers(state) {
    state.loadingUsers = true
  },
  receiveAllUsers(state) {
    state.loadingUsers = false
  },
  requestSelectedUser(state) {
    state.loadingSelectedUser = true
  },
  receiveSelectedUser(state) {
    state.loadingSelectedUser = false
  },
  requestTeams(state) {
    state.loadingTeams = true
  },
  receiveTeams(state) {
    state.loadingTeams = false
  },
  requestSelectedTeam(state) {
    state.loadingSelectedTeam = true
  },
  receiveSelectedTeam(state) {
    state.loadingSelectedTeam = false
  },
  setLoading(state, value) {
    state.loading = value
    console.log("Loading Set: " + state.loading)
  },
  toggleLoading(state) {
    state.loading = !state.loading
    console.log("Loading Toggled: " + state.loading)
  },
  toggleMode(state) {
    state.adminMode = !state.adminMode
  },
  toggleDragIcons(state, value) {
    state.hideDragIcons = value
  },
  deleteBucket(state, { caseTypeId, bucketId }) {
    const mainMenuItem = state.mainMenu.find((mmi) => mmi.caseTypeId === caseTypeId)
    if (mainMenuItem) {
      const idx = mainMenuItem.items.findIndex((mi) => mi.bucketId === bucketId)
      mainMenuItem.items.splice(idx, 1)
      Vue.set(state, "mainMenu", [...state.mainMenu])
    }
  },
  setUserPermissions(state, permissions) {
    state.grantedPermissions = (permissions || []).map((perm) => perm.key)
  },
  displayFlashMessage(state, { message, severity }) {
    state.flashMessage = {
      message: message,
      severity: severity,
      timestamp: new Date(),
    }
  },
  activateMenu(state, { menu, from, previousMenu, sameContext }) {
    state.menu = menu
    state.previousMenu = previousMenu
    if (!sameContext) {
      state.referrer = from && from.fullPath ? from.fullPath : "/"
    }
  },
  clearFlashMessage(state) {
    state.flashMessage = undefined
  },
  enableTeamFeatures(state, { currentUser, teams }) {
    let commsEnabled = false
    let metricsEnabled = false
    let chatEnabled = false
    if (currentUser.isAccountAdmin) {
      commsEnabled = true
      metricsEnabled = true
    }
    const commsEnabledTeams = (teams || []).filter((t) => t.commsEnabled)
    const metricsEnabledTeams = (teams || []).filter((t) => t.metricsEnabled)
    const chatEnabledTeams = (teams || []).filter((t) => t.chatEnabled)
    const commsEnabledTeamsCurrentUserIsMemberOf = commsEnabledTeams.filter((t) => t.users && t.users.map((u) => u.id).includes(currentUser.id))
    const metricsEnabledTeamsCurrentUserIsMemberOf = metricsEnabledTeams.filter((t) => t.users && t.users.map((u) => u.id).includes(currentUser.id))
    const chatEnabledTeamsCurrentUserIsMemberOf = chatEnabledTeams.filter((t) => t.users && t.users.map((u) => u.id).includes(currentUser.id))

    commsEnabled = commsEnabled || commsEnabledTeamsCurrentUserIsMemberOf.length > 0
    metricsEnabled = metricsEnabled || metricsEnabledTeamsCurrentUserIsMemberOf.length > 0
    chatEnabled = chatEnabledTeamsCurrentUserIsMemberOf.length > 0

    state.commsEnabled = commsEnabled
    state.metricsEnabled = metricsEnabled
    Vue.store.dispatch("setChatEnabled", chatEnabled)
  },
  setClipboardCommsMessage(state, msg) {
    state.clipboard.commsMessage = msg
  },
  setLastVisitedInbox(state, savedSearchId) {
    state.lastVisitedInboxId = savedSearchId
  },
  clearClipboard(state) {
    state.clipboard.commsMessage = null
  },
  receiveCurrentUser(state, user) {
    if (!user || !user.account || !Array.isArray(user.account.caseTypes)) return
    const userCaseTypes = user.account.caseTypes.filter((ct) => ct.coreTemplate)
    const listOfCaseTypes = user.account.caseTypes
    const ctMenus = userCaseTypes.map((ct) => {
      const listOfBucketGroups = ct.bucketGroups.map((bucketGroup) => {
        const bgBuckets = bucketGroup.buckets.map((bucket) => ({
          bucketId: bucket.id,
          containsChildItems: false,
          title: bucket.name,
          route: bucketRoute(ct, bucket),
        }))
        return {
          bucketGroupId: bucketGroup.id,
          containsChildItems: true,
          title: bucketGroup.name,
          route: bucketGroupRoute(ct, bucketGroup),
          childItems: bgBuckets,
        }
      })

      const overview = {
        title: "Overview",
        route: templateOverviewRoute(ct),
        containsChildItems: false,
      }
      const listOfItems = [overview].concat(listOfBucketGroups)

      return {
        caseTypeId: ct.id,
        title: Helpers.pluralize(Helpers.caseTypeName(ct)),
        menuGroupOpen: false,
        items: listOfItems,
        prefixDivider: true,
        icon: {
          code: ct.iconCode,
          set: ct.iconSet,
        },
      }
    })
    const bookmarks = []
    if (user.bookmarks) {
      user.bookmarks.forEach((bookmark) => {
        bookmarks.push({ id: bookmark.id, route: bookmark.route, title: bookmark.linkText })
      })
    }

    const commsroomItems = []
    if (user.account.savedSearches) {
      user.account.savedSearches.forEach((search) => {
        const label = search.contents && search.contents.total ? `${search.name} (${search.contents.total})` : search.name
        commsroomItems.push({ id: search.id, title: label, route: "/dashboard/comms/" + search.id })
      })
    }

    const workspaceItems = [
      {
        title: "Bookmarks",
        route: "/",
        childItems: bookmarks,
      },
      {
        title: "Overview",
        route: "/dashboard/overview",
      },
      {
        title: "Timeline",
        route: "/dashboard/timeline",
      },
      {
        title: "Notifications",
        route: "/dashboard/notifications",
      },
      {
        title: "Jobs",
        route: "/dashboard/jobs",
      },
      {
        title: "Kanban Board",
        route: "/kanban",
      },
    ]
    if (state.metricsEnabled) {
      workspaceItems.push({
        title: "Metrics",
        route: "/dashboard/metrics",
      })
    }
    if (state.commsEnabled) {
      if (commsroomItems && commsroomItems.length > 0) {
        workspaceItems.push({
          title: "Commsroom",
          route: "/",
          childItems: commsroomItems,
        })
      }
    }

    const caseWorkMenu = [
      {
        title: "My Workspace",
        menuGroupOpen: true,
        prefixDivider: false,
        icon: {
          code: "th-list",
          set: "fas",
        },
        items: workspaceItems,
      },
    ].concat(ctMenus)
    const configMenu = []
    const caseTemplatesMenu = []
    caseTemplatesMenu.push({
      title: "Overview",
      route: "/admin/case_types",
    })
    listOfCaseTypes.forEach((item) => {
      const scripts = [
        // { title: "Overview", route: "/admin/case_types/" + item.id + "/scripts" }
      ]
      const emails = [
        // { title: "Overview", route: "/admin/case_types/" + item.id + "/emails" }
      ]
      if (item.scripts && item.scripts.length > 0) {
        item.scripts.forEach((script) => {
          scripts.push({
            title: script.name,
            route: "/admin/case_types/" + item.id + "/scripts/" + script.id,
          })
        })
      }
      if (item.emailTemplates && item.emailTemplates.length > 0) {
        item.emailTemplates.forEach((email) => {
          emails.push({
            title: email.name,
            route: "/admin/case_types/" + item.id + "/emails/" + email.id,
          })
        })
      }
      caseTemplatesMenu.push({
        title: item.name,
        icon: {
          code: item.iconCode,
          set: item.iconSet,
        },
        route: "/admin/case_types/" + item.id,
        childItems: [
          {
            title: "Summary",
            route: "/admin/case_types/" + item.id + "/summary",
          },
          {
            title: "Schema",
            route: "/admin/case_types/" + item.id + "/schema",
          },
          {
            title: "Tasks",
            route: "/admin/case_types/" + item.id + "/tasks",
          },
          {
            title: "Relationships",
            route: "/admin/case_types/" + item.id + "/relationships",
          },
          {
            title: "Permissions",
            route: "/admin/case_types/" + item.id + "/permissions",
          },
          {
            title: "Documents",
            route: "/admin/case_types/" + item.id + "/documents",
          },
          {
            title: "Status",
            route: "/admin/case_types/" + item.id + "/status",
          },
          {
            title: "Scripts",
            route: "/admin/case_types/" + item.id + "/scripts",
            subSubItems: scripts,
          },
          {
            title: "Email Templates",
            route: "/admin/case_types/" + item.id + "/emails",
            subSubItems: emails,
          },
        ],
      })
    })

    configMenu.push({
      title: "Case Templates",
      icon: { code: "file-invoice", set: "fas" },
      items: caseTemplatesMenu,
      menuGroupOpen: true,
    })

    configMenu.push({
      title: "Admin",
      icon: { code: "users-cog", set: "fas" },
      items: accountAdminMenuItems(user.isAppAdmin, false),
      menuGroupOpen: false,
    })

    configMenu.push({
      title: "SLA",
      icon: { code: "shield-alt", set: "fas" },
      route: "/admin/sla",
    })

    configMenu.push({
      title: "Metrics",
      icon: { code: "analytics", set: "fas" },
      route: "/admin/metrics",
    })

    if (canSeeQueues(user)) {
      configMenu.push(queuesMenuItem)
    }

    // users menu
    configMenu.push({
      title: "Users",
      icon: { code: "user", set: "fas" },
      route: "/users",
    })

    // teams menu
    configMenu.push({
      title: "Teams",
      icon: { code: "users", set: "fas" },
      route: "/teams",
    })
    appendAuditNav(configMenu, user.account && user.account.auditEnabled)
    state.caseWorkMenu = caseWorkMenu
    state.configMenu = configMenu
  },
  async reloadCaseWorkMenu(state, { caseTypes, bookmarks, savedSearches }) {
    try {
      const updatedBookmarks = []
      if (bookmarks) {
        bookmarks.forEach((bookmark) => {
          updatedBookmarks.push({ id: bookmark.id, route: bookmark.route, title: bookmark.linkText })
        })
      } else {
        const childItems = state.caseWorkMenu && state.caseWorkMenu[0] && state.caseWorkMenu[0].items && state.caseWorkMenu[0].items[0] && state.caseWorkMenu[0].items[0].childItems
        updatedBookmarks.push(...(childItems || []))
      }

      const commsroomItems = []
      if (savedSearches) {
        savedSearches.forEach((search) => {
          const label = search.contents && search.contents.total ? `${search.name} (${search.contents.total})` : search.name
          commsroomItems.push({ id: search.id, title: label, route: "/dashboard/comms/" + search.id })
        })
      } else {
        const lastItem = state.caseWorkMenu && state.caseWorkMenu[0] && state.caseWorkMenu[0].items && state.caseWorkMenu[0].items[state.caseWorkMenu[0].items.length - 1]
        if (lastItem && lastItem.title === "Commsroom" && lastItem.childItems) {
          commsroomItems.push(...lastItem.childItems)
        }
      }

      const workspaceItems = [
        {
          title: "Bookmarks",
          route: "/",
          childItems: updatedBookmarks,
        },
        {
          title: "Overview",
          route: "/dashboard/overview",
        },
        {
          title: "Timeline",
          route: "/dashboard/timeline",
        },
        {
          title: "Notifications",
          route: "/dashboard/notifications",
        },
        {
          title: "Jobs",
          route: "/dashboard/jobs",
        },
      ]

      if (state.metricsEnabled) {
        workspaceItems.push({
          title: "Metrics",
          route: "/dashboard/metrics",
        })
      }
      if (Vue.store.state.chat.chatEnabled) {
        const chat = {
          title: "Chat",
          icon: {
            code: "comment",
            set: "fas",
          },
          route: "/dashboard/chat",
        }
        workspaceItems.push(chat)
      }

      // Add Boards menu item for accountId 1 (pre-release feature)
      const currentUserEmail = Vue.store.state.currentUser && Vue.store.state.currentUser.me
        ? Vue.store.state.currentUser.me.email
        : null
      const emailDomain = (typeof currentUserEmail === "string" && currentUserEmail.indexOf("@") > -1)
        ? currentUserEmail.split("@")[1]
        : null
      if (emailDomain && ["caseblocks.com", "localhost.local"].indexOf(emailDomain) > -1) {
        const boards = {
          title: "Boards",
          icon: {
            code: "table-columns",
            set: "fas",
          },
          route: "/dashboard/boards",
        }
        workspaceItems.push(boards)
      }

      const userCaseTypes = (caseTypes || []).filter((ct) => ct.coreTemplate)
      const ctMenus = userCaseTypes.map((ct) => {
        const listOfBucketGroups = ct.bucketGroups.map((bucketGroup) => {
          const bgBuckets = bucketGroup.buckets.map((bucket) => ({
            bucketId: bucket.id,
            containsChildItems: false,
            title: bucket.name,
            route: bucketRoute(ct, bucket),
          }))
          return {
            bucketGroupId: bucketGroup.id,
            containsChildItems: true,
            title: bucketGroup.name,
            route: bucketGroupRoute(ct, bucketGroup),
            childItems: bgBuckets,
          }
        })

        const overview = {
          title: "Overview",
          route: templateOverviewRoute(ct),
          containsChildItems: false,
        }
        const listOfItems = [overview].concat(listOfBucketGroups)

        return {
          caseTypeId: ct.id,
          title: Helpers.pluralize(Helpers.caseTypeName(ct)),
          menuGroupOpen: false,
          items: listOfItems,
          prefixDivider: true,
          icon: {
            code: ct.iconCode,
            set: ct.iconSet,
          },
        }
      })

      const caseWorkMenu = [
        {
          title: "My Workspace",
          menuGroupOpen: true,
          prefixDivider: false,
          icon: {
            code: "th-list",
            set: "fas",
          },
          items: workspaceItems,
        },
      ]

      if (commsroomItems.length > 0) {
        caseWorkMenu.push({
          title: "Commsroom",
          menuGroupOpen: true,
          prefixDivider: false,
          icon: {
            code: "mailbox",
            set: "fas",
          },
          items: commsroomItems,
        })
      }

      const caseTemplatesMenu = []
      caseTemplatesMenu.push({
        title: "Overview",
        route: "/admin/case_types",
      })
      state.caseWorkMenu = caseWorkMenu.concat(ctMenus)
    } catch (error) {
      console.error(error)
    }
  },
  reloadConfigMenu(state, caseTypes) {
    const configMenu = []
    const caseTemplatesMenu = []
    caseTemplatesMenu.push({ title: "Overview", route: "/admin/case_types" })
    ;(caseTypes || []).forEach((item) => {
      const scripts = []
      const emails = []
      if (item.scripts && item.scripts.length > 0) {
        item.scripts.forEach((script) => {
          scripts.push({
            title: script.name,
            route: "/admin/case_types/" + item.id + "/scripts/" + script.id,
          })
        })
      }
      if (item.emailTemplates && item.emailTemplates.length > 0) {
        item.emailTemplates.forEach((email) => {
          emails.push({
            title: email.name,
            route: "/admin/case_types/" + item.id + "/emails/" + email.id,
          })
        })
      }
      caseTemplatesMenu.push({
        title: item.name,
        icon: {
          code: item.iconCode,
          set: item.iconSet,
        },
        route: "/admin/case_types/" + item.id,
        childItems: [
          {
            title: "Summary",
            route: "/admin/case_types/" + item.id + "/summary",
          },
          {
            title: "Schema",
            route: "/admin/case_types/" + item.id + "/schema",
          },
          {
            title: "Tasks",
            route: "/admin/case_types/" + item.id + "/tasks",
          },
          {
            title: "Relationships",
            route: "/admin/case_types/" + item.id + "/relationships",
          },
          {
            title: "Permissions",
            route: "/admin/case_types/" + item.id + "/permissions",
          },
          {
            title: "Documents",
            route: "/admin/case_types/" + item.id + "/documents",
          },
          {
            title: "Status",
            route: "/admin/case_types/" + item.id + "/status",
          },
          {
            title: "Scripts",
            route: "/admin/case_types/" + item.id + "/scripts",
            subSubItems: scripts,
          },
          {
            title: "Email Templates",
            route: "/admin/case_types/" + item.id + "/emails",
            subSubItems: emails,
          },
        ],
      })
    })

    configMenu.push({
      title: "Case Templates",
      icon: { code: "stream", set: "fas" },
      items: caseTemplatesMenu,
      menuGroupOpen: true,
    })

    configMenu.push({
      title: "Admin",
      icon: { code: "users-cog", set: "fas" },
      items: accountAdminMenuItems(Vue.store && Vue.store.state && Vue.store.state.currentUser && Vue.store.state.currentUser.isAppAdmin, true),
      menuGroupOpen: false,
    })

    configMenu.push({
      title: "SLA",
      icon: { code: "shield-alt", set: "fas" },
      route: "/admin/sla",
    })

    configMenu.push({
      title: "Metrics",
      icon: { code: "analytics", set: "fas" },
      route: "/admin/metrics",
    })

    const queueUser = Vue.store && Vue.store.state && Vue.store.state.currentUser
    if (canSeeQueues(queueUser)) {
      configMenu.push(queuesMenuItem)
    }

    // teams menu
    configMenu.push({
      title: "Teams",
      icon: { code: "users", set: "fas" },
      route: "/teams",
    })

    // users menu
    configMenu.push({
      title: "Users",
      icon: { code: "user", set: "fas" },
      route: "/users",
    })

    const auditAccount = Vue.store && Vue.store.state && Vue.store.state.currentUser && Vue.store.state.currentUser.account
    appendAuditNav(configMenu, auditAccount && auditAccount.auditEnabled)
    Vue.set(state, "configMenu", configMenu)
  },
  recordLastCaseContainer(state, caseContainer) {
    state.lastCaseContainerPath.push(caseContainer)
  },
  reloadCommsRoomItems(state, data) {
    const commsroomItems = []
    const caseWorkMenu = state.caseWorkMenu
    data.newItems.forEach((search) => {
      commsroomItems.push({ id: search.id, title: search.name, route: "/dashboard/comms/" + search.id })
    })
    if (commsroomItems && commsroomItems.length > 0) {
      caseWorkMenu[0].items[5].childItems = commsroomItems
    }
    state.caseWorkMenu = caseWorkMenu
  },
  setPageTitle(state, pageTitle) {
    state.pageTitle = pageTitle
  },
  setMenuColor(state, color) {
    state.menuColor = color
  },
  setWhiteTheme(state, theme) {
    if (theme) {
      state.menuColor = "rgb(255,255,255,1)"
    } else {
      state.menuColor = "rgb(29,48,62)"
    }
    state.whiteTheme = theme
  },
  setCaseViewerObserver(state, caseViewerObserver) {
    state.caseViewerObserver = caseViewerObserver
  },
  unsetCaseViewerObserver(state) {
    state.caseViewerObserver = undefined
  },
  showMessageSidePanel(state, message) {
    state.showMessageSidePanel = true
    state.sidePanelType = "message"
    state.sidePanelContents = message
    state.keepMainMenuExpanded = true
  },
  hideSidePanel(state) {
    state.showMessageSidePanel = false
  },
  hideDocumentSidePanel(state) {
    state.showDocumentSidePanel = false
  },
  viewFileToSide(state, { field, fileAttachment }) {
    state.showDocumentSidePanel = true
    state.keepMainMenuExpanded = true
    if (!field) {
      field = { name: "Case Document" }
    }
    Vue.set(state.documentWindow, "field", field)
    Vue.set(state.documentWindow, "fileAttachment", fileAttachment)
  },
  composeNewMessage(state, { currentCase, allParticipants, allBodyTemplates, conversationId, conversationSubject, title, currentRecipients, currentRecipientsCc, currentRecipientsBcc }) {
    Vue.set(state.emailWindow, "currentCase", currentCase)
    Vue.set(state.emailWindow, "allParticipants", allParticipants)
    Vue.set(state.emailWindow, "allBodyTemplates", allBodyTemplates)
    Vue.set(state.emailWindow, "currentRecipients", currentRecipients)
    Vue.set(state.emailWindow, "currentRecipientsCc", currentRecipientsCc)
    Vue.set(state.emailWindow, "currentRecipientsBcc", currentRecipientsBcc)
    Vue.set(state.emailWindow, "emailWindowToggled", true)
    Vue.set(state.emailWindow, "conversationId", conversationId)
    Vue.set(state.emailWindow, "conversationSubject", conversationSubject)
    Vue.set(state.emailWindow, "title", title)
  },
  updateRecipientsList(state, participants) {
    Vue.set(state.emailWindow, "allParticipants", participants)
  },
  updateEmailWindowTemplates(state, rootState) {
    // Update allBodyTemplates in email window when case types change
    // rootState is passed when called from another module via { root: true }
    if (state.emailWindow.emailWindowToggled) {
      // Access caseTypes from rootState if provided, otherwise try to get from Vue.store
      let caseTypes = null
      if (rootState && rootState.caseTypes) {
        caseTypes = rootState.caseTypes
      } else if (Vue.store && Vue.store.state && Vue.store.state.caseTypes) {
        caseTypes = Vue.store.state.caseTypes
      }
      
      if (caseTypes && caseTypes.current && caseTypes.current.emailTemplates) {
        const allBodyTemplates = caseTypes.current.emailTemplates
          .filter(t => t.emailBodyTemplate)
          .map(t => ({id: t.id, name: t.name, defaultEmailBodyTemplate: t.defaultEmailBodyTemplate}))
        Vue.set(state.emailWindow, "allBodyTemplates", allBodyTemplates)
      }
    }
  },
  dismissNewMessage(state) {
    Vue.set(state.emailWindow, "emailWindowToggled", false)
    Vue.set(state.emailWindow, "currentCase", undefined)
    Vue.set(state.emailWindow, "allParticipants", [])
    Vue.set(state.emailWindow, "allBodyTemplates", [])
    Vue.set(state.emailWindow, "currentRecipients", [])
    Vue.set(state.emailWindow, "currentRecipientsCc", [])
    Vue.set(state.emailWindow, "currentRecipientsBcc", [])
    Vue.set(state.emailWindow, "conversationId", undefined)
    Vue.set(state.emailWindow, "conversationSubject", undefined)
    Vue.set(state.emailWindow, "title", "New Message")
  },
  toggleMainMenuExpansion(state) {
    state.keepMainMenuExpanded = !state.keepMainMenuExpanded
    state.keepMainMenuExpanded = true
  },
}
